/* Silamp · Remise sur quantité (affichage). La remise est appliquée par SMART Functions. */
(() => {
  if (customElements.get('silamp-remise-qte')) return;

  const VAT = 1.2;
  // Toggle HT/TTC du header : état stocké par le header ('true' = TTC, 'false' = HT)
  const isHT = () => {
    try {
      return localStorage.getItem('priceToggleState') === 'false';
    } catch (e) {
      return false;
    }
  };

  const eur = (cents) => (Math.round(cents) / 100).toFixed(2).replace('.', ',') + '€';
  const pctLabel = (p10) => '-' + String(p10 / 10).replace('.', ',') + '%';

  class SilampRemiseQte extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      let data;
      try {
        data = JSON.parse(this.querySelector('.sl-rq__data').textContent);
      } catch (e) {
        return;
      }

      this.tiers = String(data.tiers || '')
        .split(',')
        .map((s) => {
          const [m, p] = s.split(':');
          return { min: parseInt(m, 10), p10: Math.round(parseFloat(String(p).replace(',', '.')) * 10) };
        })
        .filter((t) => t.min > 1 && t.p10 > 0)
        .sort((a, b) => a.min - b.min);

      this.variants = data.variants || {};
      this.multi = !!data.multi;
      // Sur Focal, form.id renvoie le champ <input name="id"> : on passe par l'attribut
      this.formId = this.getAttribute('form-id');
      this.form = document.getElementById(this.formId);
      if (!this.tiers.length || !this.form) return;

      this.ladder = this.querySelector('.sl-rq__ladder');
      this.nudgeEl = this.querySelector('.sl-rq__nudge');
      this.saveEl = this.querySelector('.sl-rq__save');
      this.bestEl = document.querySelector(`[data-sl-rq-best="${this.formId}"]`);
      this.metaEl = document.querySelector(`product-meta.product-meta[form-id="${this.formId}"]`);
      this.priceList = this.metaEl && this.metaEl.querySelector('[data-product-price-list]');
      this.labelList = this.metaEl && this.metaEl.querySelector('[data-product-label-list]');
      const qtySelector = `input[name="quantity"][form="${this.formId}"]`;
      this.qtyInput = (this.parentElement && this.parentElement.querySelector(qtySelector)) || document.querySelector(qtySelector);
      this.atc = this.form.querySelector('[data-product-add-to-cart-button]');
      const idInput = this.form.querySelector('[name="id"]');
      this.variantId = idInput ? String(idInput.value) : null;
      this.cartItems = [];

      // Changement de variante (event Focal émis sur le form)
      this.form.addEventListener('variant:changed', (e) => {
        if (e.detail && e.detail.variant) this.variantId = String(e.detail.variant.id);
        setTimeout(() => this.render(), 0); // après la réécriture du bouton par Focal
      });

      // Quantité : les boutons +/- de Focal changent la valeur sans émettre d'événement
      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this.render());
        this.qtyInput.addEventListener('change', () => this.render());
        const selector = this.qtyInput.closest('quantity-selector');
        if (selector) selector.addEventListener('click', () => setTimeout(() => this.render(), 0));
      }

      // Clic sur un palier : on complète la quantité en tenant compte du panier
      this.ladder.addEventListener('click', (e) => {
        const cell = e.target.closest('.sl-rq__cell');
        if (!cell || cell.disabled) return;
        this.setQty(Math.max(1, parseInt(cell.dataset.min, 10) - this.inCartQty()));
      });

      // Panier : events Focal
      const onCart = (e) => (e.detail && e.detail.cart ? this.setCart(e.detail.cart) : this.fetchCart());
      document.documentElement.addEventListener('cart:updated', onCart);
      document.documentElement.addEventListener('cart:refresh', onCart);

      // Bascule HT/TTC : le header met à jour son état avant que l'événement remonte au document
      document.addEventListener('change', (e) => {
        if (e.target && e.target.id === 'priceToggle') this.render();
      });

      this.render();
      this.fetchCart();
    }

    fetchCart() {
      const url = (window.themeVariables && window.themeVariables.routes.cartUrl) || '/cart';
      fetch(`${url}.js`, { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => this.setCart(cart))
        .catch(() => {});
    }

    setCart(cart) {
      this.cartItems = (cart && cart.items) || [];
      this.render();
    }

    inCartQty() {
      return this.cartItems
        .filter((it) => String(it.variant_id) === this.variantId)
        .reduce((sum, it) => sum + it.quantity, 0);
    }

    setQty(n) {
      if (!this.qtyInput) return;
      if ('quantity' in this.qtyInput) this.qtyInput.quantity = n; // setter Focal (input-number)
      else this.qtyInput.value = n;
      this.render();
    }

    render() {
      const v = this.variants[this.variantId];
      if (!v || !v.a) {
        this.hidden = true;
        if (this.bestEl) this.bestEl.hidden = true;
        this.restoreAtc();
        return;
      }
      this.hidden = false;

      const price = v.p;
      const ht = isHT();
      const show = (c) => (ht ? `${eur(c / VAT)} HT` : eur(c));
      const unitOf = (p10) => price - Math.floor((price * p10) / 1000); // troncature SMART Functions
      const stock = v.c ? Math.max(0, v.q) : Infinity;
      // Stock sous le premier palier : aucune remise possible, on masque tout le module
      if (stock < this.tiers[0].min) {
        this.hidden = true;
        if (this.bestEl) this.bestEl.hidden = true;
        this.updateHead(null, price, show);
        this.restoreAtc();
        return;
      }
      const inCart = this.inCartQty();
      const qty = Math.max(1, parseInt(this.qtyInput ? this.qtyInput.value : 1, 10) || 1);
      const eff = inCart + qty;

      let i = -1;
      this.tiers.forEach((t, k) => { if (eff >= t.min) i = k; });
      const p10 = i >= 0 ? this.tiers[i].p10 : 0;
      let next = this.tiers[i + 1];
      if (next && next.min > stock) next = null;

      // Ligne "Dès X€/pc" sous le prix : meilleur palier atteignable, masquée dès qu'un palier est actif
      if (this.bestEl) {
        const reachable = this.tiers.filter((t) => t.min <= stock);
        const best = reachable[reachable.length - 1];
        this.bestEl.hidden = !best || i >= 0;
        if (best) {
          const pctEl = this.bestEl.querySelector('.sl-rq-best__pct');
          if (pctEl) pctEl.textContent = pctLabel(best.p10);
          this.bestEl.querySelector('.sl-rq-best__price').textContent = show(unitOf(best.p10));
        }
      }

      // Prix principal de la fiche : prix du palier atteint + prix unitaire barré
      this.updateHead(i >= 0 ? { unit: unitOf(p10), lbl: pctLabel(p10) } : null, price, show);

      // Echelle : mise à jour des cases existantes (garde le focus clavier)
      const rows = [{ min: 1, p10: 0 }].concat(this.tiers);
      let cells = Array.from(this.ladder.querySelectorAll('.sl-rq__cell'));
      if (cells.length !== rows.length) {
        this.ladder.innerHTML = rows
          .map((r, k) => `<button type="button" class="sl-rq__cell${k === 0 ? ' sl-rq__cell--base' : ''}" data-min="${r.min}"><span class="sl-rq__q"></span><span class="sl-rq__p"></span><span class="sl-rq__s"></span></button>`)
          .join('');
        cells = Array.from(this.ladder.querySelectorAll('.sl-rq__cell'));
      }
      const firstMin = this.tiers[0].min;
      rows.forEach((r, k) => {
        const cell = cells[k];
        const nextMin = rows[k + 1] ? rows[k + 1].min : Infinity;
        const passed = inCart + 1 >= nextMin; // palier déjà dépassé grâce au panier
        const noStock = r.min > stock;
        const off = passed || noStock;
        const active = k === i + 1;
        cell.dataset.min = r.min;
        cell.disabled = off;
        cell.classList.toggle('is-active', active);
        cell.setAttribute('aria-pressed', active ? 'true' : 'false');
        cell.querySelector('.sl-rq__q').textContent = k === 0 ? (firstMin > 2 ? `1 à ${firstMin - 1}` : '1') : `${r.min}+`;
        cell.querySelector('.sl-rq__p').textContent = show(unitOf(r.p10));
        cell.querySelector('.sl-rq__s').textContent = passed ? 'Déjà dépassé' : noStock ? `${stock} en stock` : k === 0 ? 'Prix de base' : pctLabel(r.p10);
      });

      // Message
      const pcs = (n) => `${n} pièce${n > 1 ? 's' : ''}`;
      let cartTier = -1; // palier atteint par le panier seul
      this.tiers.forEach((t, k) => { if (inCart >= t.min) cartTier = k; });
      let nudge = '';
      if (inCart && i > cartTier) {
        // L'ajout en cours fait franchir un palier à tout le panier : c'est le message prioritaire
        nudge = `Les ${eff} pièces (dont ${inCart} au panier) passent à ${show(unitOf(p10))} l'unité`;
      } else if (next) {
        const target = next.min - inCart; // quantité à saisir pour atteindre le palier suivant
        const offer = `${pcs(target)} : ${show(unitOf(next.p10))} l'unité`;
        if (inCart) nudge = `Ajoutez-en ${target} : les ${next.min} (dont ${inCart} au panier) passent à ${show(unitOf(next.p10))} l'unité`;
        else if (i < 0) nudge = `Dès ${pcs(next.min)}${this.multi ? ' identiques' : ''} : ${pctLabel(next.p10)} sur chacune, sans code`;
        else nudge = `Passez à ${offer}`;
      }
      this.nudgeEl.textContent = nudge;
      this.saveEl.textContent = i >= 0 ? `Vous économisez ${show(eff * (price - unitOf(p10)))}` : '';

      this.updateAtc(show(qty * unitOf(p10)));
    }
    updateHead(tier, base, show) {
      const list = this.priceList;
      if (!list) return;
      const main = list.querySelector('.price--large') || list.querySelector('.price');
      if (!main) return;
      (this.metaEl || document).querySelectorAll('.sl-rq-head, .sl-rq-head__tag').forEach((el) => el.remove());
      // Format attendu par le toggle HT/TTC du header : "4,89"
      const raw = (c) => (Math.round(c) / 100).toFixed(2).replace('.', ',');

      if (!tier) {
        // Toujours réécrire : corrige aussi le prix qui repassait en TTC après un changement de variante en mode HT
        main.setAttribute('data-price', raw(base));
        main.textContent = show(base);
        return;
      }

      main.setAttribute('data-price', raw(tier.unit));
      main.textContent = show(tier.unit);

      // Prix de base barré, seulement si le produit n'a pas déjà un prix barré promo
      const compare = list.querySelector('.price--compare');
      if (!compare) {
        const was = document.createElement('s');
        was.className = 'sl-rq-head';
        was.textContent = show(base);
        main.insertAdjacentElement('afterend', was);
      }

      // Badge rangé avec les étiquettes du thème (à côté du -73%)
      const tag = document.createElement('span');
      tag.className = 'sl-rq-head__tag';
      tag.textContent = compare ? `${tier.lbl} volume` : tier.lbl;
      if (this.labelList) this.labelList.prepend(tag);
      else (compare || main).insertAdjacentElement('afterend', tag);
    }

    updateAtc(totalLabel) {
      const btn = this.atc;
      if (!btn || btn.disabled) return;
      const txt = btn.querySelector('.loader-button__text') || btn;
      txt.textContent = `${btn.getAttribute('data-button-content') || ''} · ${totalLabel}`;
    }

    restoreAtc() {
      const btn = this.atc;
      if (!btn || btn.disabled) return;
      const txt = btn.querySelector('.loader-button__text') || btn;
      txt.textContent = btn.getAttribute('data-button-content') || '';
    }
  }

  customElements.define('silamp-remise-qte', SilampRemiseQte);
})();